import React, { useReducer, createContext, useContext, useMemo } from 'react';

import { createRouteParamHook, createSingleQueryParamHook } from '@confluence/route-manager';

import {
	PageAnalyticsTab,
	PageAnalyticsLinksSubTab,
} from '../components/PageAnalytics/pageAnalyticsConstants';

import {
	type PageAnalyticsActionType,
	pageAnalyticsReducer,
	buildInitialPageAnalyticsStore,
} from './pageAnalyticsReducer';
import type { DateRangeState } from './storeTypes';
import type { InjectedPageAnalyticsProps } from './PageDataInjector';

type BasePageAnalyticsStoreType = DateRangeState & {
	isAnyFilterUpdated: boolean;
};

export type PageAnalyticsStoreType = BasePageAnalyticsStoreType & InjectedPageAnalyticsProps;

const PageAnalyticsContext = createContext<{
	state: PageAnalyticsStoreType;
	dispatch: React.Dispatch<PageAnalyticsActionType>;
}>({
	// Real contentId will be injected as soon as we get it from the container props
	state: buildInitialPageAnalyticsStore(),
	dispatch: () => null,
});

export const PageAnalyticsStoreProvider: React.FC<InjectedPageAnalyticsProps> = ({
	children,
	...injectedProps
}) => {
	const [state, dispatch] = useReducer(
		pageAnalyticsReducer,
		buildInitialPageAnalyticsStore(injectedProps),
	);

	const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

	return (
		<PageAnalyticsContext.Provider value={contextValue}>{children}</PageAnalyticsContext.Provider>
	);
};

export const usePageAnalyticsStore = () => useContext(PageAnalyticsContext);

const useRouteParamHook = createRouteParamHook('tab');
export const usePageAnalyticsTab = () => {
	const selectedTabFromRouteParam = useRouteParamHook();
	switch (selectedTabFromRouteParam) {
		case 'overview':
			return PageAnalyticsTab.Overview;
		case 'mentions':
			return PageAnalyticsTab.Mentions;
		case 'links':
			return PageAnalyticsTab.Links;
		default:
			return PageAnalyticsTab.Overview;
	}
};

const useRouteQueryParamHook = createSingleQueryParamHook('type');
export const usePageAnalyticsLinksSubTab = () => {
	const tab = usePageAnalyticsTab();
	const selectedSubTabFromRouteQueryParam = useRouteQueryParamHook();
	if (tab !== PageAnalyticsTab.Links) {
		return undefined;
	}

	switch (selectedSubTabFromRouteQueryParam) {
		case 'outbound':
			return PageAnalyticsLinksSubTab.Outbound;
		case 'inbound':
			return PageAnalyticsLinksSubTab.Inbound;
		default:
			return PageAnalyticsLinksSubTab.Outbound;
	}
};
